import React, { useState, useEffect } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaFutbol,
  FaSearch,
  FaHistory,
  FaExclamationTriangle,
  FaFilter
} from "react-icons/fa";
import { useArenaAuth } from "../../context/ArenaAuthContext";

const BookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const { arenaUser } = useArenaAuth();

  useEffect(() => {
    const fetchBookingHistory = async () => {
      try {
        const arenaId = arenaUser?.arenaId;
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/arena-bookingRequests?arenaId=${arenaId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch booking requests");
        }
        const data = await response.json();

        // Update the status of each booking based on current time and logic
        const updatedBookings = data.map((booking) => ({
          ...booking,
          status: getEffectiveStatus(booking),
        }));

        setBookings(updatedBookings);
        console.log(updatedBookings);
      } catch (error) {
        console.error("Error fetching booking requests:", error);
      }
    };

    if (arenaUser?.arenaId) fetchBookingHistory();
  }, [arenaUser?.arenaId]);

  const isBookingTimePassed = (booking) => {
    try {
      // Parse the time range (e.g., "09:00:00-09:30:00")
      const [startTime] = booking.time.split('-');
      const bookingDateTime = new Date(`${booking.date}T${startTime}`);
      const now = new Date();
      return bookingDateTime < now;
    } catch (error) {
      console.error('Error parsing booking time:', error);
      return false;
    }
  };

  const isBookingCompleted = (booking) => {
    try {
      // Parse the time range to get end time
      const [, endTime] = booking.time.split('-');
      const bookingEndDateTime = new Date(`${booking.date}T${endTime}`);
      const now = new Date();
      return bookingEndDateTime < now && booking.status.toLowerCase() === 'approved';
    } catch (error) {
      console.error('Error parsing booking end time:', error);
      return false;
    }
  };

  const getEffectiveStatus = (booking) => {
    // If booking is completed (approved and end time passed)
    if (isBookingCompleted(booking)) {
      return 'completed';
    }
    // If status is pending and start time has passed, show as "missed"
    if (booking.status.toLowerCase() === 'pending' && isBookingTimePassed(booking)) {
      return 'missed';
    }
    // If status is "cancelled by user", keep it
    if (booking.status.toLowerCase() === 'cancelled by user') {
      return 'cancelled by user';
    }
    return booking.status.toLowerCase();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.sport.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter =
      filter === "all" ||
      (filter === "upcoming" &&
        (booking.status === "pending" || booking.status === "approved")) ||
      (filter === "completed" && booking.status === "completed") ||
      booking.status.toLowerCase() === filter.toLowerCase();
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaHistory className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Booking History</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">View and track all booking requests and their status</p>
      </div>

      <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 md:mb-8">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 md:gap-6">
          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaSearch className="text-[#0071bc] text-xs sm:text-sm" />
              Search Bookings
            </label>
            <div className="relative">
              <FaSearch className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
              <input
                type="text"
                placeholder="Search by user or sport"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-7 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaFilter className="text-green-500 text-xs sm:text-sm" />
              Filter by Status
            </label>
            <select
              value={filter}
              onChange={handleFilterChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 bg-white"
            >
              <option value="all">All Bookings</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled by user">Cancelled by User</option>
              <option value="missed">Missed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        {filteredBookings.map((booking) => {
          const getStatusColor = (status) => {
            switch (status) {
              case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
              case 'approved': return 'bg-blue-100 text-blue-800 border-blue-200';
              case 'completed': return 'bg-green-100 text-green-800 border-green-200';
              case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
              case 'cancelled by user': return 'bg-gray-100 text-gray-800 border-gray-200';
              case 'missed': return 'bg-orange-100 text-orange-800 border-orange-200';
              default: return 'bg-gray-100 text-gray-800 border-gray-200';
            }
          };
          
          return (
            <div key={booking.id.join("-")} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 md:gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                      <FaUser className="text-white text-xs sm:text-sm md:text-lg" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 truncate">{booking.user}</h3>
                      <div className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(booking.status)}`}>
                        {booking.status === 'cancelled by user' ? 'Cancelled by User' : 
                         booking.status === 'missed' ? 'Missed' :
                         booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <FaFutbol className="text-green-500 text-xs sm:text-sm flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Sport</span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base truncate">{booking.sport}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <FaCalendarAlt className="text-blue-500 text-xs sm:text-sm flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Date</span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{booking.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700 sm:col-span-2 md:col-span-1">
                      <FaClock className="text-purple-500 text-xs sm:text-sm flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Time</span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{booking.time}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredBookings.length === 0 && (
        <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
          <FaExclamationTriangle className="text-4xl sm:text-5xl md:text-6xl text-gray-400 mx-auto mb-2 sm:mb-3 md:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No Bookings Found</h3>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base px-4">No bookings match your current search and filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
